// Phase 6b — render-job (edit) data layer over the Flask JSON API via the 6a
// `api()` helper (Accept:json + credentials + CSRF + centralized 401).

import { api } from './api'
import { appFetch } from './http'
import { apiUrl } from './native'
import { notifySeriesChanged } from './seriesEvents'
import type { Edit, EditsPage, EditorConfig, RenderJobStatus, RenderLyrics } from './types'

/** GET /api/render — paginated, newest-first render jobs for the tenant. */
export function fetchEdits(opts: { limit?: number; offset?: number } = {}): Promise<EditsPage> {
  const params = new URLSearchParams()
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  const qs = params.toString()
  return api<EditsPage>('/api/render' + (qs ? '?' + qs : ''))
}

/** GET /api/render/<id>/download — re-mint a short-lived presigned URL on demand. */
export async function fetchDownloadUrl(id: string): Promise<string> {
  const r = await api<{ download_url: string }>(`/api/render/${id}/download`)
  return r.download_url
}

/** GET /api/render/<id> — poll one job's status. */
export function fetchRenderJob(id: string): Promise<RenderJobStatus> {
  return api<RenderJobStatus>(`/api/render/${id}`)
}

/** GET /api/render/<id>/lyrics — the words snapshot this render burned into the
 * MP4 (karaoke preview) + the source music_id (deep-link to the lyrics editor). */
export function fetchRenderLyrics(id: string): Promise<RenderLyrics> {
  return api<RenderLyrics>(`/api/render/${id}/lyrics`)
}

/** Thrown when POST /api/render returns 402 (empty wallet). */
export class InsufficientTokensError extends Error {
  constructor() {
    super('insufficient_tokens')
    this.name = 'InsufficientTokensError'
  }
}

/** Thrown when POST /api/render returns 400 (bad editor config / idempotency_key). */
/** 429 too_many_inflight — the render was refused BEFORE any spend.
 *
 *  Its own class because the difference is about money: a failed render is
 *  charged and then refunded, while this one never charged anything. Falling
 *  through to the generic failure made the UI announce a refund of gems that had
 *  never left the wallet. */
export class TooManyInflightError extends Error {
  constructor() {
    super('too_many_inflight')
    this.name = 'TooManyInflightError'
  }
}

export class InvalidConfigError extends Error {
  constructor(message = 'invalid_config') {
    super(message)
    this.name = 'InvalidConfigError'
  }
}

/** Exact 409 pack_unavailable response: validation refused before any spend. */
export class PackUnavailableError extends InvalidConfigError {
  constructor() {
    super('pack_unavailable')
    this.name = 'PackUnavailableError'
  }
}

export type TemplateValueField = 'artist' | 'timestamp' | '@nick' | 'title'
export type TemplateValuesErrorCode = 'missing_template_values' | 'invalid_template_values'

/** A validation refusal happens before enqueue and spend. Keep it out of RenderFailed. */
export class TemplateValuesError extends InvalidConfigError {
  readonly code: TemplateValuesErrorCode
  readonly fields: TemplateValueField[]

  constructor(code: TemplateValuesErrorCode, fields: unknown) {
    super(code)
    this.name = 'TemplateValuesError'
    this.code = code
    const allowed = new Set<TemplateValueField>(['artist', 'timestamp', '@nick', 'title'])
    this.fields = Array.isArray(fields)
      ? [...new Set(fields.filter((field): field is TemplateValueField => typeof field === 'string' && allowed.has(field as TemplateValueField)))]
      : []
  }
}

/** POST /api/render 202 body: back-compat top-level id/status (= first job) plus
 * the full batch list (P03 `count`). */
export interface CreateRenderResult {
  id: string
  status: string
  /** The batch this Generate click created — where the UI navigates next
   * (/batches/<id>). Null only when a replayed pre-0023 job was returned. */
  batch_id: string | null
  jobs: { id: string; status: string }[]
}

/**
 * POST /api/render — spend `count` gems (default 1), queue `count` renders.
 * Mints a client-side `idempotency_key` (uuid) so a double-click is replay-safe
 * (the backend derives per-job keys from it for batches). Translates the 402/400
 * backend responses into typed errors the screens branch on.
 *
 * Uses a raw fetch (not api()) so we can read the 402/400 status BEFORE api()
 * collapses everything non-2xx into a generic `HTTP <n>`. It still mirrors api()'s
 * CSRF handling: on a stale token (419/403, common ~1h into an open tab) it
 * force-refreshes the token and retries the POST ONCE — the idempotency_key is
 * reused so the retry can't double-spend.
 */
export async function createRender(
  editorConfig: EditorConfig,
  count = 1,
  title?: string | null,
  idempotencyKey?: string,
  appendToSourceSeries = false,
): Promise<CreateRenderResult> {
  const idempotency_key = idempotencyKey || crypto.randomUUID()
  const cleanTitle = title?.trim()
  const body = JSON.stringify({
    editor_config: editorConfig,
    idempotency_key,
    count,
    ...(cleanTitle ? { title: cleanTitle } : {}),
    ...(appendToSourceSeries ? { retry_in_series: true } : {}),
  })
  const send = (csrf: string) =>
    appFetch(apiUrl('/api/render'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': csrf,
      },
      body,
    })

  let res = await send(await getCsrf())
  // Stale/expired CSRF token → refetch once and retry the POST (same idem key).
  if (res.status === 419 || res.status === 403) {
    res = await send(await getCsrf(true))
  }

  if (res.status === 402) throw new InsufficientTokensError()
  if (res.status === 429) throw new TooManyInflightError()
  if (res.status === 400) throw new InvalidConfigError()
  if (res.status === 409) {
    const payload = await res.json().catch(() => null) as { error?: unknown } | null
    if (payload?.error === 'pack_unavailable') throw new PackUnavailableError()
  }
  if (res.status === 422) {
    const payload = await res.json().catch(() => null) as { error?: unknown; fields?: unknown } | null
    if (payload?.error === 'missing_template_values' || payload?.error === 'invalid_template_values') {
      throw new TemplateValuesError(payload.error, payload.fields)
    }
  }
  if (res.status === 401) {
    // Defer to api()'s centralized 401 handling by routing one no-op call.
    await api('/auth/me')
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const result = (await res.json()) as CreateRenderResult
  notifySeriesChanged()
  return result
}

/**
 * DELETE /api/render/<id> — remove a terminal render (job + MP4). The backend
 * answers 409 render_in_flight while it is still queued/rendering; api() surfaces
 * that as ApiError(409) for the screen to message.
 */
export function deleteRenderJob(id: string): Promise<void> {
  return api(`/api/render/${id}`, { method: 'DELETE' }).then(() => undefined)
}

// `createRender` needs the raw fetch (to read 402/400 status before api() throws
// a generic HTTP error). It still attaches the CSRF token the same way api() does.
async function getCsrf(force = false): Promise<string> {
  const { getCsrfToken } = await import('./csrf')
  return getCsrfToken(force)
}

export type { Edit }
